import { Inject, Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { tag } from 'rxjs-spy/operators';
import { Observable, defer, from, of } from 'rxjs';
import { switchMap, tap, startWith, map, distinctUntilChanged, takeWhile } from 'rxjs/operators';
import { TranslateService } from '@ngx-translate/core';

import { DialogService } from 'rev-shared/ui/dialog/Dialog.Service';
import { DisconnectCallback, PushService } from 'rev-shared/push/PushService';
import { SignalRHubsConnection } from 'rev-shared/push/SignalRHubsConnection';
import { SignalRHubsConnectionToken } from 'rev-shared/push/SignalRHubsConnectionToken';
import { UserContextService } from 'rev-shared/security/UserContext.Service';
import { addTeardown } from 'rev-shared/rxjs/AddTeardownOperator';
import { lastValueFrom } from 'rev-shared/rxjs/lastValueFrom';

import { WebcastPlayerLoggingService } from 'rev-portal/scheduledEvents/webcast/WebcastPlayerLogging.Service';
import { WebcastKeepAliveHandler } from 'rev-portal/scheduledEvents/webcast/WebcastKeepAliveHandler';

import { WebcastModel } from './WebcastModel';

@Injectable({
	providedIn: 'root'
})
export class WebcastConnectionService {
	private readonly accountId = this.UserContext.getAccount().id;
	private keepAliveHandler;

	constructor(
		private readonly Dialog: DialogService,
		private readonly http: HttpClient,
		private readonly PushService: PushService,
		@Inject(SignalRHubsConnectionToken) private readonly SignalRHubsConnection: SignalRHubsConnection,
		private readonly TranslateService: TranslateService,
		private readonly UserContext: UserContextService,
		private readonly WebcastPlayerLoggingService: WebcastPlayerLoggingService
	) {}

	public connect(webcast: WebcastModel): Observable<any> {
		return webcast.webcast$.pipe(
			map(w => w.currentUser.isEventAdmin),
			distinctUntilChanged(),
			switchMap(() => this.getAttendeeConnection(webcast)),
			tag('WebcastConnectionService.connect')
		);
	}

	private getAttendeeConnection(webcast: WebcastModel): Observable<any> {
		return this.getWebcastConnection(webcast,
			() => {
				return this.dispatchJoinWebcast(webcast)
					.then(() => this.startKeepAlive(webcast));
			},
			() => this.registerAttendeeDisconnectCommand(webcast),
			() => {
				return new Promise(resolve => {
					this.stopKeepAlive();
					resolve(webcast.currentUser.sessionId ?
						this.disconnectWebcastAttendee(webcast) :
						null);
				});

			}
		);
	}

	private getWebcastConnection(
		webcast: WebcastModel,
		joinFn: () => Promise<void>,
		autoDisconnectFn: () => Promise<DisconnectCallback>,
		leaveFn: () => Promise<void>
	): Observable<any> {
		let disconnectFn: DisconnectCallback;

		const autoDisconnnect$ = defer(autoDisconnectFn).pipe(
			tap(result => disconnectFn = result)
		);

		const join$ = defer(joinFn).pipe(
			switchMap(() => autoDisconnnect$)
		);

		return this.SignalRHubsConnection.reconnected$.pipe(
			startWith<any, any>(undefined),
			takeWhile(() => !webcast.isPastEndDate(), true),
			switchMap(() => {
				if(webcast.isPastEndDate()) {
					return of(undefined);
				}

				if(!disconnectFn) {
					return join$;
				}

				return from(disconnectFn()).pipe(
					switchMap(success => success ?
						autoDisconnnect$ :
						join$)
				);
			}),
			addTeardown(() => {
				if(disconnectFn) {
					disconnectFn();
				}
				leaveFn();
			}),
			tag('WebcastConnection.getWebcastConnection')
		);
	}

	private dispatchJoinWebcast(webcast: WebcastModel): Promise<any> {
		const command = webcast.isTrustedPublicWebcast ? 'scheduledEvents:JoinTrustedPublicWebcast' : 'scheduledEvents:JoinWebcast';
		if(!webcast.currentUser.sessionId) {
			webcast.currentUser.sessionId = [webcast.id, webcast.currentRun.runNumber, webcast.currentUser.id, this.UserContext.getSessionStartTime()].join('_');
		}

		return this.handleUserExistingSession(webcast)
			.then(() => {
				return this.PushService.dispatchCommand(command, {
					webcastId: webcast.id,
					sessionId: webcast.currentUser.sessionId,
					runNumber: webcast.currentRun.runNumber,
					viewContext: webcast.analyticsViewContext
				});
			});
	}

	private registerAttendeeDisconnectCommand(webcast: WebcastModel): Promise<DisconnectCallback> {
		const webcastId = webcast.id;
		return this.PushService.registerDisconnectCommand('scheduledEvents:DisconnectWebcastAttendee', {
			webcastId,
			userId: webcast.currentUser.id,
			sessionId: webcast.currentUser.sessionId,
			accountId: this.accountId,
			runNumber: webcast.currentRun.runNumber
		});
	}

	private disconnectWebcastAttendee(webcast: WebcastModel): Promise<void> {

		return this.PushService.dispatchCommand('scheduledEvents:DisconnectWebcastAttendee', {
			webcastId: webcast.id,
			userId: webcast.currentUser.id,
			sessionId: webcast.currentUser.sessionId,
			runNumber: webcast.currentRun.runNumber
		});
	}

	//As of now handling of existing session only for pre-registered guest.
	private handleUserExistingSession(webcast: WebcastModel): Promise<void> {
		if (this.UserContext.isUserAuthenticated()) {
			return Promise.resolve();
		}

		return this.fetchUserSession(webcast.id, webcast.currentRun.runNumber)
			.then(({ session }) => {
				if (!session || session.sessionId === webcast.currentUser.sessionId || session.joinCount === 0) {
					return;
				}

				return webcast.currentUser.isExternalPresenter ? this.showExternalPresenterPopup() : this.showSessionExistsPopup();
			});
	}

	private fetchUserSession(webcastId: string, runNumber: number): Promise<any> {
		return lastValueFrom(this.http.get<any>(`/scheduled-events/${webcastId}/${runNumber}/user/session`));
	}

	private showSessionExistsPopup(): Promise<any> {
		const params = {
			message: this.TranslateService.instant('Event_Session_AlreadyJoined_Message'),
			title: this.TranslateService.instant('Event_Session_AlreadyJoined'),
			cancelText: this.TranslateService.instant('Cancel'),
			actionText: this.TranslateService.instant('Ok'),
			themed: true
		};

		return this.Dialog.openConfirmationDialog(params).result
			.catch(() => this.handleCancelPopup());
	}

	private showExternalPresenterPopup(): Promise<any> {
		const params = {
			message: this.TranslateService.instant('Event_Session_ExternalPresenter_AlreadyJoined_Message'),
			title: this.TranslateService.instant('Event_Session_AlreadyJoined'),
			actionText: this.TranslateService.instant('Ok'),
			themed: true
		};

		return this.Dialog.openConfirmationDialog(params).result
			.finally(() => this.handleCancelPopup());
	}

	private handleCancelPopup(): void {
		this.UserContext.logOutUser()
			.then(() => window.location.reload()); //this will redirect user to guest login.
	}

	private startKeepAlive(webcast: WebcastModel): void {
		if(!this.keepAliveHandler) {
			this.keepAliveHandler = new WebcastKeepAliveHandler(
				this.WebcastPlayerLoggingService,
				webcast.currentUser.sessionId,
				webcast.id,
				webcast.currentRun.runNumber
			);
		}
		this.keepAliveHandler.startKeepAlive();
	}

	private stopKeepAlive(): void {
		if(this.keepAliveHandler) {
			this.keepAliveHandler.stopKeepAlive();
			this.keepAliveHandler = null;
		}
	}
}
